import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { formatDateRange } from './utils';
import { NETWORK_NAME, PLATFORM_NAME } from './constants';
import MultiProgressBar from './MultiProgressBar';
import { AdShareResult } from './types';
interface AdShareTabProps {
  selectedWeek: { week: number; startDate: string; endDate: string };
  adShareData: AdShareResult;
}

const AdShareTab: React.FC<AdShareTabProps> = ({ selectedWeek, adShareData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const plotData = adShareData?.data;

  const countryColorArray = ['#008080', '#B27B00', '#EB8B00', '#FFC84D', '#CABF9B'];

  const channelColorArray = ['#008080', '#B27B00', '#EB8B00', '#FFC84D', '#CABF9B'];
  const platformsColorArray = ['#0072B2', '#40676E'];

  const selectedWeekNumber = selectedWeek.week;

  const countryData = plotData?.country_data?.find(
    (item: any) => item.week_number === selectedWeekNumber,
  );
  const channelData = plotData?.network_data?.find(
    (item: any) => item.week_number === selectedWeekNumber,
  );
  const platformData = plotData?.platform_data?.find(
    (item: any) => item.week_number === selectedWeekNumber,
  );

  const transformData = (data: any[]) => {
    if (data.length <= 5) {
      return data;
    }

    const firstFour = data.slice(0, 4);
    const others = data.slice(4);

    const othersSummary = others.reduce(
      (acc, item) => {
        acc.raw_score += item.raw_score;
        acc.percentage += item.percentage;
        return acc;
      },
      {
        country: 'Others',
        network: 'Others',
        platform: 'Others',
        country_code: 'OT',
        raw_score: 0,
        percentage: 0,
      },
    );

    return [...firstFour, othersSummary];
  };

  const transformedCountryData = countryData?.data
    ? transformData(countryData.data)
    : [{ country: 'Worldwide', percentage: 100 }]; //In case there is no country split data, we show worldwide with 100%

  const transformedChannelData = channelData?.data ? transformData(channelData.data) : [];

  const transformedPlatformData = platformData?.data ? transformData(platformData.data) : [];

  const countryProgressData = transformedCountryData.map((item, index) => ({
    name: item.country,
    value: item.percentage.toFixed(0),
    color: countryColorArray[index],
  }));

  const channelProgressData = transformedChannelData.map((item, index) => ({
    name: NETWORK_NAME[item.network as keyof typeof NETWORK_NAME],
    value: item.percentage.toFixed(0),
    color: channelColorArray[index],
  }));

  const platformProgressData = transformedPlatformData.map((item, index) => ({
    name: PLATFORM_NAME[item.platform as keyof typeof PLATFORM_NAME],
    value: item.percentage.toFixed(0),
    color: platformsColorArray[index],
  }));

  const noData =
    countryProgressData.length === 0 &&
    channelProgressData.length === 0 &&
    platformProgressData.length === 0;

  return (
    <Box
      sx={{
        backgroundColor: '#F0F0F1',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      {noData ? (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          No data
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.25rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#162936',
                lineHeight: '1.25rem',
              }}
            >
              Week {selectedWeekNumber} &#x2022;
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#6C6C6C',
                lineHeight: '1.25rem',
              }}
            >
              {formatDateRange(`${selectedWeek.startDate} - ${selectedWeek.endDate}`)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1.5rem',
              '& > *': {
                width: isMobile ? '100%' : '33.33%',
              },
            }}
          >
            <MultiProgressBar
              progressTitle="Top Countries"
              progressData={countryProgressData}
              showTooltip={true}
            />
            {channelProgressData.length > 0 ? (
              <MultiProgressBar
                progressTitle="Top Channels"
                progressData={channelProgressData}
                showTooltip={true}
              />
            ) : (
              <></>
            )}
            {platformProgressData.length > 0 ? (
              <MultiProgressBar
                progressTitle="Platforms"
                progressData={platformProgressData}
                showTooltip={true}
              />
            ) : (
              <></>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdShareTab;
